import {
  mockStripeProductsData,
  mockStripeConfigData,
} from "../data/mockData/wlDemoData";
import { handlePatchSubUser, getSubUserRows } from "./subUserDemoHelpers";
import { getMergedDemoUser, patchDemoUserTwoFactor } from "./demoTwoFactorHelpers";

const STORAGE_CONFIG = "wl_bs_stripe_config";
const STORAGE_PRODUCTS = "wl_bs_stripe_products";
const STORAGE_PRODUCTS_DELETED = "wl_bs_stripe_products_deleted";

const REQUIRED_WEBHOOK_EVENTS = [
  "customer.subscription.created",
  "customer.subscription.updated",
  "customer.subscription.deleted",
  "invoice.paid",
  "invoice.payment_failed",
];

function readJson(key, fallback) {
  if (typeof sessionStorage === "undefined") {
    return fallback;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) {
      return fallback;
    }
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

function normalizePayload(data) {
  if (data == null) {
    return {};
  }
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return data;
}

function getDeletedProductIds() {
  return new Set((readJson(STORAGE_PRODUCTS_DELETED, []) || []).map(String));
}

function mergeProducts() {
  const deleted = getDeletedProductIds();
  const overrides = readJson(STORAGE_PRODUCTS, {});
  const base = (mockStripeProductsData || []).filter(
    (row) => row && row.id != null && !deleted.has(String(row.id))
  );
  const map = new Map();
  base.forEach((row) => {
    map.set(String(row.id), { ...row, ...(overrides[String(row.id)] || {}) });
  });
  Object.keys(overrides || {}).forEach((id) => {
    if (!map.has(id) && !deleted.has(id)) {
      map.set(id, overrides[id]);
    }
  });
  return Array.from(map.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function getNextProductId(rows) {
  return (
    (rows || []).reduce((max, row) => {
      const val = Number(row?.id) || 0;
      return val > max ? val : max;
    }, 500) + 1
  );
}

function buildSubscriptionFromProduct(product, status = "active") {
  if (!product) {
    return null;
  }
  const now = new Date();
  const periodEnd = new Date(now);
  periodEnd.setMonth(periodEnd.getMonth() + (product.interval === "year" ? 12 : 1));
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  return {
    plan_name: product.name,
    status,
    product_id: product.id,
    subscription_id: `sub_demo_${product.id}`,
    amount: product.amount,
    currency: product.currency || "eur",
    interval: product.interval || "month",
    song_limit: product.song_limit,
    artist_limit: product.artist_limit,
    music_link_limit: product.music_link_limit,
    link_landing_page_limit: product.link_landing_page_limit,
    music_link_enabled: !!product.music_link_enabled,
    link_landing_page_enabled: !!product.link_landing_page_enabled,
    features: product.features || [],
    deal_percent: product.deal_percent,
    current_period_end: periodEnd.toISOString(),
    cancel_at_period_end: false,
    billing_portal_url: `${origin}/subscription`,
  };
}

function applySubscriptionFields(patch, product, status = "active") {
  const subscription = buildSubscriptionFromProduct(product, status);
  return {
    ...patch,
    wl_user_type: "subscription",
    tenant_subscription_plan: subscription?.plan_name || null,
    tenant_subscription_status: subscription?.status || null,
    tenant_subscription: subscription,
    track_allowance: Number(product?.song_limit) || 0,
    artist_allowance: Number(product?.artist_limit) || 0,
    distribution: (Number(product?.song_limit) || 0) > 0,
    music_link: !!product?.music_link_enabled,
    link_in_bio: !!product?.link_landing_page_enabled,
  };
}

export function getDemoStripeConfig() {
  const overrides = readJson(STORAGE_CONFIG, {});
  return {
    ...mockStripeConfigData,
    ...overrides,
    required_webhook_events:
      overrides.required_webhook_events ||
      mockStripeConfigData.required_webhook_events ||
      REQUIRED_WEBHOOK_EVENTS,
  };
}

export function saveDemoStripeConfig(payload = {}) {
  const body = normalizePayload(payload);
  const current = getDemoStripeConfig();
  const next = { ...current };

  if (body.secret_key) {
    const key = String(body.secret_key).trim();
    next.has_secret_key = true;
    next.stripe_account_name = "Demo Stripe Account";
    next.stripe_livemode = key.startsWith("sk_live");
    next.secret_key_hint = `${key.slice(0, 7)}••••`;
  }

  if (body.webhook_signing_secret) {
    next.has_webhook_signing_secret = true;
    next.webhook_signing_secret_hint = "whsec_••••";
  }

  if (body.currency) {
    next.currency = String(body.currency).toLowerCase();
  }

  if (body.tax_calculation_method) {
    next.tax_calculation_method = body.tax_calculation_method;
  }

  writeJson(STORAGE_CONFIG, next);
  return getDemoStripeConfig();
}

export function syncDemoStripeWebhook() {
  const current = getDemoStripeConfig();
  if (!current.has_secret_key) {
    throw new Error("Connect a Stripe secret key before syncing the webhook.");
  }

  const next = {
    ...current,
    webhook_endpoint_id: current.webhook_endpoint_id || "we_demo_endpoint",
    webhook_manual_required: false,
    has_webhook_signing_secret: true,
    webhook_signing_secret_hint: "whsec_••••",
    required_webhook_events: REQUIRED_WEBHOOK_EVENTS,
    webhook_url:
      current.webhook_url ||
      "https://api.example-label.test/wl-tenant-stripe-webhook/1/",
  };
  writeJson(STORAGE_CONFIG, next);
  return getDemoStripeConfig();
}

export function listDemoStripeProducts() {
  return mergeProducts();
}

export function getDemoStripeProduct(id) {
  return mergeProducts().find((row) => String(row.id) === String(id)) || null;
}

export function createDemoStripeProduct(payload = {}) {
  const body = normalizePayload(payload);
  const rows = mergeProducts();
  if (rows.length >= 5) {
    throw new Error("A maximum of 5 subscription products is allowed.");
  }
  const id = getNextProductId(rows);
  const row = {
    id,
    name: String(body.name || "").trim() || `Plan ${id}`,
    description: String(body.description || "").trim(),
    features: Array.isArray(body.features) ? body.features : [],
    amount: Number(body.amount) || 0,
    currency: String(body.currency || getDemoStripeConfig().currency || "eur").toLowerCase(),
    interval: body.interval === "year" ? "year" : "month",
    deal_percent: Number(body.deal_percent) || 0,
    song_limit: Number(body.song_limit) || 0,
    artist_limit: Number(body.artist_limit) || 0,
    music_link_limit: Number(body.music_link_limit) || 0,
    link_landing_page_limit: Number(body.link_landing_page_limit) || 0,
    music_link_enabled: !!body.music_link_enabled,
    link_landing_page_enabled: !!body.link_landing_page_enabled,
    order: rows.length,
    is_active: true,
  };
  const overrides = readJson(STORAGE_PRODUCTS, {});
  overrides[String(id)] = row;
  writeJson(STORAGE_PRODUCTS, overrides);
  return row;
}

export function updateDemoStripeProduct(id, payload = {}) {
  const existing = getDemoStripeProduct(id);
  if (!existing) {
    throw new Error("Product not found.");
  }
  const body = normalizePayload(payload);
  const next = {
    ...existing,
    ...body,
    id: existing.id,
    features: Array.isArray(body.features) ? body.features : existing.features,
    amount: body.amount != null ? Number(body.amount) : existing.amount,
    deal_percent:
      body.deal_percent != null ? Number(body.deal_percent) : existing.deal_percent,
    song_limit: body.song_limit != null ? Number(body.song_limit) : existing.song_limit,
    artist_limit:
      body.artist_limit != null ? Number(body.artist_limit) : existing.artist_limit,
    music_link_limit:
      body.music_link_limit != null
        ? Number(body.music_link_limit)
        : existing.music_link_limit,
    link_landing_page_limit:
      body.link_landing_page_limit != null
        ? Number(body.link_landing_page_limit)
        : existing.link_landing_page_limit,
    music_link_enabled:
      body.music_link_enabled != null
        ? !!body.music_link_enabled
        : existing.music_link_enabled,
    link_landing_page_enabled:
      body.link_landing_page_enabled != null
        ? !!body.link_landing_page_enabled
        : existing.link_landing_page_enabled,
  };
  const overrides = readJson(STORAGE_PRODUCTS, {});
  overrides[String(id)] = next;
  writeJson(STORAGE_PRODUCTS, overrides);
  return next;
}

export function deleteDemoStripeProduct(id) {
  const existing = getDemoStripeProduct(id);
  if (!existing) {
    throw new Error("Product not found.");
  }
  const deleted = readJson(STORAGE_PRODUCTS_DELETED, []);
  if (!deleted.map(String).includes(String(id))) {
    deleted.push(Number(id) || id);
    writeJson(STORAGE_PRODUCTS_DELETED, deleted);
  }
  const overrides = readJson(STORAGE_PRODUCTS, {});
  delete overrides[String(id)];
  writeJson(STORAGE_PRODUCTS, overrides);
  return { status: "ok" };
}

export function listDemoPublicPlans() {
  return mergeProducts()
    .filter((row) => row.is_active !== false)
    .map((row, index) => ({
      ...row,
      order: row.order ?? index,
    }));
}

/**
 * Simulate Stripe checkout locally: assign the plan to the current demo user
 * (when they are a sub-user) and return a success redirect URL.
 */
export function createDemoCheckoutSession(productId, urls = {}) {
  const product = getDemoStripeProduct(productId);
  if (!product) {
    throw new Error("Product not found.");
  }

  const user = getMergedDemoUser();
  if (user?.is_sub_user) {
    const fields = applySubscriptionFields({}, product, "active");
    patchDemoUserTwoFactor(fields);
    // Keep sub-user list in sync when the logged-in demo identity matches a seed row.
    const match = getSubUserRows().find(
      (row) =>
        String(row.id) === String(user.id) ||
        String(row.email || "").toLowerCase() === String(user.email || "").toLowerCase()
    );
    if (match) {
      handlePatchSubUser(match.id, fields);
    }
  }

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  const successUrl =
    urls.success_url || `${origin}/subscription/payment/success`;

  return { url: successUrl, id: `cs_demo_${productId}_${Date.now()}` };
}

/**
 * When branding enables subscriptions, apply legacy/subscription transition
 * to all seeded/session sub-users.
 */
export function applyExistingUsersSubscriptionTransition(transition) {
  const mode = String(transition || "").toLowerCase();
  if (mode !== "legacy" && mode !== "subscription") {
    return;
  }

  getSubUserRows().forEach((row) => {
    if (!row?.id) {
      return;
    }
    if (mode === "legacy") {
      handlePatchSubUser(row.id, {
        wl_user_type: "legacy",
      });
      return;
    }

    // subscription-only: clear plan until they subscribe
    handlePatchSubUser(row.id, {
      wl_user_type: "subscription",
      tenant_subscription_plan: null,
      tenant_subscription_status: null,
      tenant_subscription: null,
      distribution: false,
      track_allowance: 0,
      artist_allowance: 0,
    });
  });
}

export function clearDemoStripeSubscriptionStorage() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.removeItem(STORAGE_CONFIG);
  sessionStorage.removeItem(STORAGE_PRODUCTS);
  sessionStorage.removeItem(STORAGE_PRODUCTS_DELETED);
}
